import React, { useCallback, useContext, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Select from '@amzn/meridian/select';
import SelectOption from '@amzn/meridian/select/select-option';
import Text from '@amzn/meridian/text';
import alertKnockoutTokens from '@amzn/meridian-tokens/base/icon/alert-knockout';
import { useTranslation } from 'src/arb-wrapper';
import { ID_TYPES, CAMPAIGN_PAGE_TYPE } from 'src/constants';
import { FormInput } from 'src/components/common/forms/FormInput';
import { FormGtiInput } from 'src/components/common/forms/FormGtiInput';
import { FormPageIdSelect } from 'src/components/campaigns/forms/FormPageIdSelect';
import { type FormRhfControllerProps } from 'src/components/common/forms/types';
import { FormProductStrategyContext } from 'src/components/campaigns/forms/FormProductStrategy';

export const FromSonataIdEntry = ({
  control,
  index,
  pageIdFieldName,
  strategyIdFieldName,
  gtiListFieldName,
}: Partial<FormRhfControllerProps> & {
  index: number;
  pageIdFieldName: string;
  strategyIdFieldName: string;
  gtiListFieldName: string;
}) => {
  const { t } = useTranslation();

  const {
    idType,
    hideLatestTitlesData,
    isDebouncing,
    isError,
    isFetching,
    isLoading,
    pageId,
    sonataIdErrorMsg,
    strategyId,
    titles,
    setIdType,
    onGtiValidateModeChange,
    gtiTitleMap,
  } = useContext(FormProductStrategyContext);

  const idValidator = useCallback(() => !isError, [isError]);

  // For GTI List, don't use the API validator (it's for pageId/strategyId only)
  const validator = useMemo(() => {
    if (idType === ID_TYPES.GTI_LIST) {
      return undefined;
    }
    return {
      fn: idValidator,
      message: sonataIdErrorMsg,
    };
  }, [idType, idValidator, sonataIdErrorMsg]);

  const showTitles = useMemo(
    () => (!isLoading && !isError && !isDebouncing && (pageId ?? strategyId) && !hideLatestTitlesData && idType !== ID_TYPES.GTI_LIST),
    [hideLatestTitlesData, isDebouncing, isError, isLoading, pageId, strategyId, idType]
  );

  const idTypeOptions = useMemo(() => {
    return [
      { label: t('form_sonata_strategy_id_label'), value: ID_TYPES.STRATEGY_ID },
      { label: t('form_sonata_page_id_label'), value: ID_TYPES.PAGE_ID },
      { label: t('form_gti_list_label'), value: ID_TYPES.GTI_LIST },
    ];
  }, [t]);

  return (
    <>
      <Row
        alignmentHorizontal="justify"
        alignmentVertical="top"
        widths={['fill', 'fill']}
      >
        <Column spacing="200">
          <Row alignmentVertical="center" spacing="100">
            <Text>{`${t('form_title_source_label')}*`}</Text>
          </Row>
          <Select
            data-testid={`product-strategy-id-type-${index}`}
            value={idType}
            onChange={setIdType}
          >
          {idTypeOptions.map(({ label, value }) => (
            <SelectOption
              data-testid={`product-strategy-id-type-option-${value}`}
              key={value}
              label={label}
              value={value}
            />
          ))}
          </Select>
        </Column>

        {idType === ID_TYPES.PAGE_ID && (
          <FormPageIdSelect
            control={control}
            index={index}
            name={pageIdFieldName}
            pageType={CAMPAIGN_PAGE_TYPE}
            required
            testIdPrefix="product-strategy-page-id"
          />
        )}

        {idType === ID_TYPES.STRATEGY_ID && (
          <FormInput
            control={control}
            data-testid={`product-strategy-strategy-id-${index}`}
            key={`product-strategy-strategy-id-${index}`}
            label={t('form_sonata_strategy_id_label')}
            name={strategyIdFieldName}
            placeholder={t('form_sonata_strategy_id_placeholder')}
            required
            trim
            validator={validator}
          />
        )}

        {idType === ID_TYPES.GTI_LIST && (
          <div style={{ visibility: 'hidden', width: '100%' }} />
        )}
      </Row>

      {idType === ID_TYPES.GTI_LIST && (
        <FormGtiInput
          control={control}
          data-testid={`product-strategy-gti-list-${index}`}
          gtiTitleMap={gtiTitleMap}
          isFetching={isFetching}
          key={`product-strategy-gti-list-${index}`}
          label={t('form_gti_list_label')}
          name={gtiListFieldName}
          required
          validator={validator}
          onValidateModeChange={onGtiValidateModeChange}
        />
      )}

      {idType !== ID_TYPES.GTI_LIST && (
        <Row
          alignmentHorizontal="justify"
          alignmentVertical="top"
          backgroundColor="subdued"
        >
          <Column width="100%">
            <Row spacingInset="400 400 none 400">
              <Text type="h50">{t('form_campaign_titles_label')}{showTitles && titles?.length && ` (${titles.length})`}</Text>
            </Row>
            <Row
              data-testid={`product-strategy-titles-${index}`}
              overflowX="scroll"
            >
              {isLoading && (
                <Column spacingInset="400">
                  <MeridianLoader size="small" />
                </Column>
              )}
              {(!isLoading && isError) && (
                <Column spacingInset="400">
                  <Icon
                    color="error"
                    tokens={alertKnockoutTokens}
                  />
                </Column>
              )}
              {showTitles && titles?.map(title => (
                <img
                  alt={title.title ?? ''}
                  key={title.title}
                  src={title.coverImageUrl ?? ''}
                  style={{ maxWidth: '172px' }}
                  title={title.title ?? ''}
                />
              ))}
            </Row>
          </Column>
        </Row>
      )}
    </>
  );
};
